import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, BarChart3, Contact, LifeBuoy, Receipt, Megaphone, SlidersHorizontal, ShieldAlert, BookOpen, ShieldCheck, GitBranch, ChevronDown, ChevronRight, Users } from "lucide-react";
import { ChangeManagementBoard, ChangeMetricsPanel, ChangeMetricsDrilldown } from "@/pages/admin/ChangeManagementBoard";
import { UsersPanel, SupportTicketsPanel, TransactionsPanel, PromosManager, PricingSettings, ModerationLog, CoursesPanel } from "@/pages/Admin";
import { useAdminStats, useAdminTickets } from "@/hooks/useAdmin";

// New Admin Command Center — structural shell only.
// Zone A: fixed left sidebar (empty). Zone B: top ~1/3 with 3-tab switcher whose
// content toggles. Zone C: bottom ~2/3 persistent workspace (never toggles).
// Palette: #F5F2EA background with charcoal (#2A2A28) accents. Responsive with a
// mobile bottom dock standing in for the sidebar.

const BG = "#F5F2EA";
const CHARCOAL = "#2A2A28";

const TABS = [
  { id: "executive", label: "Executive Dashboard", Icon: BarChart3 },
  { id: "crm", label: "Users", Icon: Users },
  { id: "support", label: "Support Team", Icon: LifeBuoy },
  { id: "transactions", label: "Transactions", Icon: Receipt },
  { id: "promos", label: "Promos", Icon: Megaphone },
  { id: "pricing", label: "Pricing", Icon: SlidersHorizontal },
  { id: "safety", label: "Safety Logs", Icon: ShieldAlert },
  { id: "courses", label: "Courses", Icon: BookOpen },
  { id: "admin", label: "Admin Dashboard", Icon: ShieldCheck },
  { id: "change", label: "Change Management", Icon: GitBranch },
];

const TAB_BY_ID = Object.fromEntries(TABS.map((t) => [t.id, t]));

// CRM Tools expandable group — children shown alphabetically by label.
const CRM_CHILD_IDS = ["support", "transactions", "crm"];
// Sidebar structure: flat items plus the CRM Tools group inserted in place.
const SIDEBAR_NAV = [
  { kind: "item", id: "executive" },
  { kind: "group", id: "crmGroup", label: "CRM Tools", Icon: Contact, children: CRM_CHILD_IDS },
  { kind: "item", id: "promos" },
  { kind: "item", id: "pricing" },
  { kind: "item", id: "safety" },
  { kind: "item", id: "courses" },
  { kind: "item", id: "admin" },
  { kind: "item", id: "change" },
];

// Executive KPI placeholder cards (labels only — no data yet).
const EXEC_CARDS = [
  { id: "total-revenue", title: "Total Revenue", labels: ["YTD", "QTD", "MTD", "Learner", "Scholar", "SMB"] },
  { id: "total-users", title: "Total Users", labels: ["Learners", "Scholars", "SMB Accounts", "SMB Users"] },
  { id: "unique-topics", title: "Journeys", labels: [] },
  { id: "platform-headroom", title: "Platform Headroom", subtitle: "Spend vs. Anticipated Needs", labels: [] },
];

const CATEGORY_META = [
  { key: "Coins", color: "#C79A3A" },
  { key: "Suggestion", color: "#4C8BF5" },
  { key: "Stuck", color: "#D64545" },
  { key: "Billing", color: "#3AA76D" },
];
const PAIN_COLORS = { 1: "#3AA76D", 2: "#8FB84E", 3: "#C79A3A", 4: "#E07B39", 5: "#D64545" };

function SupportTriageInsights({ filter, onFilterChange }) {
  const { data: tickets = [] } = useAdminTickets();
  const total = tickets.length;
  const catCounts = CATEGORY_META.map((c) => ({ ...c, count: tickets.filter((t) => t.question_category === c.key).length }));
  const uncategorized = tickets.filter((t) => !t.question_category).length;
  const catMax = Math.max(1, uncategorized, ...catCounts.map((c) => c.count));
  const painCounts = [1, 2, 3, 4, 5].map((n) => ({ n, count: tickets.filter((t) => t.customer_pain === n).length }));
  const untriagedPain = tickets.filter((t) => !t.customer_pain).length;
  const painMax = Math.max(1, ...painCounts.map((p) => p.count));

  const toggleCat = (key) => onFilterChange({ ...filter, category: filter.category === key ? null : key });
  const togglePain = (n) => onFilterChange({ ...filter, pain: filter.pain === n ? null : n });

  if (total === 0) {
    return (
      <div className="h-full min-h-[120px] flex items-center" data-testid="support-triage-insights-empty">
        <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>No tickets yet — triage insights will appear here as tickets come in.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="support-triage-insights">
      {/* By Category */}
      <div data-testid="triage-insight-category">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CHARCOAL }}>By Category · <span className="font-normal normal-case" style={{ color: "rgba(42,42,40,0.5)" }}>click to filter</span></span>
          <span className="text-[11px]" style={{ color: "rgba(42,42,40,0.5)" }}>{total} tickets</span>
        </div>
        <div className="space-y-2">
          {catCounts.map((c) => {
            const active = filter.category === c.key;
            return (
              <button key={c.key} type="button" onClick={() => toggleCat(c.key)}
                className={`w-full flex items-center gap-2 rounded-sm px-1 py-0.5 -mx-1 transition-colors ${active ? "ring-2" : "hover:bg-black/5"}`}
                style={active ? { boxShadow: `0 0 0 2px ${c.color}` } : undefined}
                data-testid={`triage-cat-${c.key}`}>
                <span className="w-20 text-xs shrink-0 text-left" style={{ color: CHARCOAL, fontWeight: active ? 700 : 400 }}>{c.key}</span>
                <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ backgroundColor: "rgba(42,42,40,0.06)" }}>
                  <div className="h-full rounded-sm transition-all" style={{ width: `${(c.count / catMax) * 100}%`, backgroundColor: c.color }} />
                </div>
                <span className="w-6 text-right text-xs font-bold" style={{ color: CHARCOAL }}>{c.count}</span>
              </button>
            );
          })}
          {uncategorized > 0 && (
            <button type="button" onClick={() => toggleCat("__untagged__")}
              className={`w-full flex items-center gap-2 rounded-sm px-1 py-0.5 -mx-1 transition-colors ${filter.category === "__untagged__" ? "ring-2" : "hover:bg-black/5"}`}
              style={filter.category === "__untagged__" ? { boxShadow: "0 0 0 2px rgba(42,42,40,0.4)" } : undefined}
              data-testid="triage-cat-uncategorized">
              <span className="w-20 text-xs shrink-0 text-left" style={{ color: "rgba(42,42,40,0.45)" }}>Untagged</span>
              <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ backgroundColor: "rgba(42,42,40,0.06)" }}>
                <div className="h-full rounded-sm" style={{ width: `${(uncategorized / catMax) * 100}%`, backgroundColor: "rgba(42,42,40,0.25)" }} />
              </div>
              <span className="w-6 text-right text-xs font-bold" style={{ color: "rgba(42,42,40,0.45)" }}>{uncategorized}</span>
            </button>
          )}
        </div>
      </div>

      {/* By Customer Pain */}
      <div data-testid="triage-insight-pain">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: CHARCOAL }}>By Customer Pain · <span className="font-normal normal-case" style={{ color: "rgba(42,42,40,0.5)" }}>click to filter</span></span>
          {untriagedPain > 0 && <span className="text-[11px]" style={{ color: "rgba(42,42,40,0.5)" }}>{untriagedPain} untriaged</span>}
        </div>
        <div className="flex items-end justify-between gap-2 h-[104px]">
          {painCounts.map((p) => {
            const active = filter.pain === p.n;
            return (
              <button key={p.n} type="button" onClick={() => togglePain(p.n)}
                className={`flex-1 flex flex-col items-center justify-end gap-1 h-full rounded-sm transition-colors ${active ? "ring-2" : "hover:bg-black/5"}`}
                style={active ? { boxShadow: `0 0 0 2px ${PAIN_COLORS[p.n]}` } : undefined}
                data-testid={`triage-pain-${p.n}`}>
                <span className="text-xs font-bold" style={{ color: CHARCOAL }}>{p.count}</span>
                <div className="w-full rounded-t-sm transition-all" style={{ height: `${(p.count / painMax) * 72}px`, minHeight: p.count ? 4 : 0, backgroundColor: PAIN_COLORS[p.n] }} />
                <span className="text-[11px]" style={{ color: "rgba(42,42,40,0.6)", fontWeight: active ? 700 : 400 }}>{p.n}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] mt-2 text-center" style={{ color: "rgba(42,42,40,0.45)" }}>1 = low · 5 = severe</p>
      </div>
    </div>
  );
}

function RevenueCard() {
  const rows = ["Learner", "Scholar", "SMB"];
  const cols = ["TTD", "YTD", "QTD", "MTD"];
  return (
    <div
      className="rounded-lg border bg-white/50 p-4 flex flex-col"
      style={{ borderColor: "rgba(42,42,40,0.16)" }}
      data-testid="kpi-card-total-revenue"
    >
      <h3 className="text-base font-bold tracking-tight" style={{ color: CHARCOAL }} data-testid="kpi-title-total-revenue">
        Total Revenue
      </h3>
      {/* Tiny table: rows = Learner/Scholar/SMB, cols = TTD/YTD/QTD/MTD */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-[11px]" data-testid="revenue-table">
          <thead>
            <tr style={{ color: "rgba(42,42,40,0.55)" }}>
              <th className="text-left font-semibold py-1 pr-2" />
              {cols.map((c) => (
                <th key={c} className="text-right font-semibold py-1 px-2 select-text" data-testid={`revenue-col-${c.toLowerCase()}`}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r} className="border-t" style={{ borderColor: "rgba(42,42,40,0.1)" }} data-testid={`revenue-row-${r.toLowerCase()}`}>
                <td className="text-left font-medium py-1.5 pr-2 select-text" style={{ color: CHARCOAL }}>{r}</td>
                {cols.map((c) => (
                  <td key={c} className="text-right py-1.5 px-2" style={{ color: "rgba(42,42,40,0.4)" }} data-testid={`revenue-cell-${r.toLowerCase()}-${c.toLowerCase()}`}>
                    —
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersCard() {
  const rows = ["Learners", "Scholars", "SMB Accounts", "SMB Users"];
  return (
    <div
      className="rounded-lg border bg-white/50 p-4 flex flex-col"
      style={{ borderColor: "rgba(42,42,40,0.16)" }}
      data-testid="kpi-card-total-users"
    >
      <h3 className="text-base font-bold tracking-tight" style={{ color: CHARCOAL }} data-testid="kpi-title-total-users">
        Total Users
      </h3>
      <div className="mt-3">
        <table className="w-full border-collapse text-[11px]" data-testid="users-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r} className="border-t" style={{ borderColor: "rgba(42,42,40,0.1)" }} data-testid={`users-row-${r.toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="text-left font-medium py-1.5 pr-2 select-text" style={{ color: CHARCOAL }}>{r}</td>
                <td className="text-right py-1.5 pl-2" style={{ color: "rgba(42,42,40,0.4)" }} data-testid={`users-value-${r.toLowerCase().replace(/\s+/g, "-")}`}>—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JourneysCard() {
  const rows = ["Grasshopper", "Journeyman", "Expert", "Master"];
  return (
    <div
      className="rounded-lg border bg-white/50 p-4 flex flex-col"
      style={{ borderColor: "rgba(42,42,40,0.16)" }}
      data-testid="kpi-card-unique-topics"
    >
      <h3 className="text-base font-bold tracking-tight" style={{ color: CHARCOAL }} data-testid="kpi-title-unique-topics">
        Journeys
      </h3>
      <div className="mt-3">
        <table className="w-full border-collapse text-[11px]" data-testid="journeys-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r} className="border-t" style={{ borderColor: "rgba(42,42,40,0.1)" }} data-testid={`journeys-row-${r.toLowerCase()}`}>
                <td className="text-left font-medium py-1.5 pr-2 select-text" style={{ color: CHARCOAL }}>{r}</td>
                <td className="text-right py-1.5 pl-2" style={{ color: "rgba(42,42,40,0.4)" }} data-testid={`journeys-value-${r.toLowerCase()}`}>—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ card }) {
  return (
    <div
      className="rounded-lg border bg-white/50 p-4 flex flex-col"
      style={{ borderColor: "rgba(42,42,40,0.16)" }}
      data-testid={`kpi-card-${card.id}`}
    >
      <h3 className="text-sm font-bold tracking-tight" style={{ color: CHARCOAL }} data-testid={`kpi-title-${card.id}`}>
        {card.title}
      </h3>
      {card.subtitle && (
        <p className="mt-1 text-xs" style={{ color: "rgba(42,42,40,0.6)" }} data-testid={`kpi-subtitle-${card.id}`}>
          {card.subtitle}
        </p>
      )}
      {card.labels.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {card.labels.map((l) => (
            <span
              key={l}
              className="text-[11px] font-medium px-2 py-1 rounded border select-text"
              style={{ color: "rgba(42,42,40,0.75)", borderColor: "rgba(42,42,40,0.18)" }}
              data-testid={`kpi-label-${card.id}-${l.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCommandCenter() {
  const [activeTab, setActiveTabState] = useState("executive");
  const [crmOpen, setCrmOpen] = useState(false);
  const crmExpanded = crmOpen || CRM_CHILD_IDS.includes(activeTab);
  const [supportFilter, setSupportFilter] = useState({ category: null, pain: null });
  const [changeFilter, setChangeFilter] = useState(null);
  const [changeRefresh, setChangeRefresh] = useState(0);
  const bumpChange = useCallback(() => setChangeRefresh((v) => v + 1), []);
  const setActiveTab = (id) => { setChangeFilter(null); setActiveTabState(id); };
  const { data: stats } = useAdminStats();
  const openTickets = stats?.open_tickets ?? 0;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: BG }} data-testid="admin-command-center">
      {/* Zone A — Left Nav (fixed width, empty) */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 border-r px-4 py-6"
        style={{ borderColor: "rgba(42,42,40,0.14)" }}
        data-testid="zone-a-sidebar"
      >
        <Link
          to="/admin/console"
          data-testid="legacy-admin-link"
          className="inline-flex items-center gap-2 text-sm font-semibold px-1 py-1 mb-3 transition-opacity hover:opacity-70"
          style={{ color: CHARCOAL }}
        >
          <ArrowLeftRight className="h-4 w-4" /> Legacy Admin
        </Link>
        <div className="h-11 flex items-center px-1" data-testid="zone-a-brand">
          <span className="text-lg font-bold tracking-tight" style={{ color: CHARCOAL }}>Admin Tools</span>
        </div>
        <div className="mt-5 border-t" style={{ borderColor: "rgba(42,42,40,0.12)" }} />
        <nav className="flex-1 mt-4 flex flex-col gap-1" data-testid="zone-a-nav" aria-label="Admin navigation">
          {SIDEBAR_NAV.map((node) => {
            if (node.kind === "item") {
              const { id, label, Icon } = TAB_BY_ID[node.id];
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  data-testid={`nav-item-${id}`}
                  aria-current={active ? "page" : undefined}
                  className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-semibold text-left transition-colors"
                  style={{ color: active ? "#F5F2EA" : CHARCOAL, backgroundColor: active ? CHARCOAL : "transparent" }}
                >
                  <Icon className="h-4 w-4 shrink-0" /> {label}
                </button>
              );
            }
            // CRM Tools group
            const GroupIcon = node.Icon;
            return (
              <div key={node.id}>
                <button
                  onClick={() => setCrmOpen((v) => !v)}
                  data-testid="nav-group-crm"
                  aria-expanded={crmExpanded}
                  className="w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-semibold text-left transition-colors"
                  style={{ color: CHARCOAL, backgroundColor: "transparent" }}
                >
                  <GroupIcon className="h-4 w-4 shrink-0" /> {node.label}
                  {crmExpanded ? <ChevronDown className="h-4 w-4 ml-auto shrink-0" /> : <ChevronRight className="h-4 w-4 ml-auto shrink-0" />}
                </button>
                {crmExpanded && (
                  <div className="mt-1 flex flex-col gap-1" data-testid="nav-group-crm-children">
                    {node.children.map((childId) => {
                      const { id, label, Icon } = TAB_BY_ID[childId];
                      const active = activeTab === id;
                      return (
                        <button
                          key={id}
                          onClick={() => setActiveTab(id)}
                          data-testid={`nav-item-${id}`}
                          aria-current={active ? "page" : undefined}
                          className="flex items-center gap-2.5 rounded-md pl-9 pr-3 py-2 text-sm font-semibold text-left transition-colors"
                          style={{ color: active ? "#F5F2EA" : CHARCOAL, backgroundColor: active ? CHARCOAL : "transparent" }}
                        >
                          <Icon className="h-4 w-4 shrink-0" /> {label}
                          {id === "support" && openTickets > 0 && (
                            <span
                              data-testid="support-unread-badge"
                              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full"
                              style={{ backgroundColor: "#D64545", color: "#F5F2EA" }}
                            >
                              {openTickets}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="border-t pt-4" style={{ borderColor: "rgba(42,42,40,0.12)" }} data-testid="zone-a-footer" />
      </aside>

      {/* Main area */}
      <main className="flex-1 flex flex-col gap-4 p-4 md:p-6 pb-24 md:pb-6 min-w-0">
        {/* Zone B — Dashboards (top ~1/3, toggles by tab) */}
        <section
          className={`rounded-xl border bg-white/40 flex flex-col overflow-hidden md:min-h-[240px] ${activeTab === "change" ? "" : "md:h-[34vh]"}`}
          style={{ borderColor: "rgba(42,42,40,0.16)" }}
          data-testid="zone-b-section"
        >
          <div className="flex border-b overflow-x-auto" style={{ borderColor: "rgba(42,42,40,0.12)" }} role="tablist" data-testid="zone-b-tabs">
            {TABS.map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className="relative px-5 sm:px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors"
                  style={{ color: active ? CHARCOAL : "rgba(42,42,40,0.55)" }}
                >
                  {t.label}
                  {t.id === "support" && openTickets > 0 && (
                    <span
                      data-testid="support-unread-badge-tab"
                      className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full align-middle"
                      style={{ backgroundColor: "#D64545", color: "#F5F2EA" }}
                    >
                      {openTickets}
                    </span>
                  )}
                  {active && (
                    <span className="absolute left-0 right-0 -bottom-px h-0.5" style={{ backgroundColor: CHARCOAL }} />
                  )}
                </button>
              );
            })}
          </div>
          {/* Toggled content per tab */}
          <div className="flex-1 p-6 overflow-y-auto" data-testid="zone-b-content">
            {activeTab === "executive" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="zone-b-panel-executive">
                {EXEC_CARDS.map((c) => (
                  c.id === "total-revenue"
                    ? <RevenueCard key={c.id} />
                    : c.id === "total-users"
                    ? <UsersCard key={c.id} />
                    : c.id === "unique-topics"
                    ? <JourneysCard key={c.id} />
                    : <KpiCard key={c.id} card={c} />
                ))}
              </div>
            )}
            {activeTab === "support" && (
              <div className="h-full" data-testid="zone-b-panel-support">
                <SupportTriageInsights filter={supportFilter} onFilterChange={setSupportFilter} />
              </div>
            )}
            {activeTab === "admin" && <div className="h-full min-h-[120px]" data-testid="zone-b-panel-admin" />}
            {activeTab === "crm" && (
              <div className="h-full min-h-[120px] flex items-center" data-testid="zone-b-panel-crm">
                <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>
                  <strong style={{ color: CHARCOAL }}>CRM Tools</strong> — manage users, adjust Learn Me Coins, message individuals or send bulk notifications. The full user workspace runs below.
                </p>
              </div>
            )}
            {activeTab === "transactions" && (
              <div className="h-full min-h-[120px] flex items-center" data-testid="zone-b-panel-transactions">
                <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>
                  <strong style={{ color: CHARCOAL }}>Transactions</strong> — every payment and coin purchase, with filters and one-click refunds. The full ledger runs below.
                </p>
              </div>
            )}
            {activeTab === "promos" && (
              <div className="h-full min-h-[120px] flex items-center" data-testid="zone-b-panel-promos">
                <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>
                  <strong style={{ color: CHARCOAL }}>Promos</strong> — edit the "From Learn Me" promo rail. Changes go live on the site instantly.
                </p>
              </div>
            )}
            {activeTab === "pricing" && (
              <div className="h-full min-h-[120px] flex items-center" data-testid="zone-b-panel-pricing">
                <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>
                  <strong style={{ color: CHARCOAL }}>Pricing</strong> — set the coin-to-USD ratio, per-service coin costs, and feature bounty. Changes affect live pricing.
                </p>
              </div>
            )}
            {activeTab === "safety" && (
              <div className="h-full min-h-[120px] flex items-center" data-testid="zone-b-panel-safety">
                <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>
                  <strong style={{ color: CHARCOAL }}>Safety Logs</strong> — prompts the Family Safe filter flagged or rejected. Monitor here for repeat offenders and bad actors.
                </p>
              </div>
            )}
            {activeTab === "courses" && (
              <div className="h-full min-h-[120px] flex items-center" data-testid="zone-b-panel-courses">
                <p className="text-sm" style={{ color: "rgba(42,42,40,0.6)" }}>
                  <strong style={{ color: CHARCOAL }}>Courses</strong> — every generated course. Mark support courses, review duration/format, or remove a course. The full log runs below.
                </p>
              </div>
            )}
            {activeTab === "change" && (
              <div className="h-full" data-testid="zone-b-panel-change">
                <ChangeMetricsPanel filter={changeFilter} onDrill={setChangeFilter} refreshToken={changeRefresh} />
              </div>
            )}
          </div>
        </section>

        {/* Zone C — Workspace (persistent). Hosts the Change Management pipeline. */}
        <section
          className="flex-1 min-h-[280px] rounded-xl border bg-white/40 p-4 flex flex-col"
          style={{ borderColor: "rgba(42,42,40,0.16)" }}
          data-testid="zone-c-workspace"
        >
          {activeTab === "change" ? (
            changeFilter ? (
              <ChangeMetricsDrilldown filter={changeFilter} onClose={() => setChangeFilter(null)} onChange={bumpChange} refreshToken={changeRefresh} />
            ) : (
              <ChangeManagementBoard onChange={bumpChange} />
            )
          ) : activeTab === "crm" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-crm">
              <UsersPanel />
            </div>
          ) : activeTab === "support" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-support">
              <SupportTicketsPanel filter={supportFilter} onFilterChange={setSupportFilter} />
            </div>
          ) : activeTab === "transactions" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-transactions">
              <TransactionsPanel />
            </div>
          ) : activeTab === "promos" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-promos">
              <PromosManager />
            </div>
          ) : activeTab === "pricing" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-pricing">
              <PricingSettings />
            </div>
          ) : activeTab === "safety" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-safety">
              <ModerationLog />
            </div>
          ) : activeTab === "courses" ? (
            <div className="flex-1 overflow-y-auto" data-testid="zone-c-courses">
              <CoursesPanel />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "rgba(42,42,40,0.4)" }} data-testid="zone-c-idle">
              Select "Change Management" or "CRM Tools" above to open the workspace.
            </div>
          )}
        </section>
      </main>

      {/* Mobile bottom dock — stands in for Zone A on small screens (empty) */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 h-16 border-t flex items-stretch"
        style={{ backgroundColor: BG, borderColor: "rgba(42,42,40,0.16)" }}
        data-testid="mobile-dock"
        aria-label="Admin navigation"
      >
        {TABS.map(({ id, label, Icon }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              data-testid={`nav-dock-${id}`}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors"
              style={{ color: active ? CHARCOAL : "rgba(42,42,40,0.5)" }}
            >
              <Icon className="h-4 w-4" />
              {label.replace(" Dashboard", "")}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
